import {
  Bell,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  LogOut,
  Menu,
  Settings,
  User,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth, usePage } from '../../contexts';
import { useTokens } from '../../hooks';
import { BuyTokensModal, LanguageSelector, ThemeToggle, TokensProgressBar } from '../ui';
import { isOrganizationAdmin } from '../../utils/organizationRole';

interface HeaderProps {
  onMenuClick: () => void;
  isSidebarCollapsed: boolean;
  onToggleSidebarCollapse: () => void;
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  isSidebarCollapsed,
  onToggleSidebarCollapse,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { title, description, headerLeftActions, headerRightActions } = usePage();
  const { isBuyTokensModalOpen, closeBuyTokensModal } = useTokens();

  const canAccessOrganizationMenu = isOrganizationAdmin(user);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  return (
    <header className="bg-white dark:bg-graphite-900 shadow-sm border-b border-light-200 dark:border-graphite-700">
      <div className="flex items-center justify-between h-16 px-3 lg:px-3">
        {/* Left side */}
        <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-600 dark:hover:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-800"
            >
              <Menu className="h-6 w-6" />
            </button>

          {headerLeftActions ? <div className="flex items-center ml-2">{headerLeftActions}</div> : null}

          <button
            onClick={onToggleSidebarCollapse}
            className="hidden lg:flex p-2 rounded-md text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-600 dark:hover:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-800"
            title={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-label={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isSidebarCollapsed ? (
              <ChevronsRight className="h-5 w-5" />
            ) : (
              <ChevronsLeft className="h-5 w-5" />
            )}
          </button>

          <div className="hidden lg:block ml-3">
            <h1 className="text-lg font-semibold text-deepTechBlue-500 dark:text-light-50">
              {title}
            </h1>
            <p className="text-sm text-deepTechBlue-500 dark:text-light-400">
              {description}
            </p>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-3">
          {headerRightActions ? (
            <div className="flex items-center space-x-2">{headerRightActions}</div>
          ) : null}

          {/* Tokens Progress Bar */}
          <TokensProgressBar className="hidden md:flex" />

          {/* Language Selector */}
          <LanguageSelector showLabel={false} className="w-auto" />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <button className="p-2 text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-600 dark:hover:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-800 rounded-md">
            <Bell className="h-5 w-5" />
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-800 rounded-md"
            >
              <div className="h-8 w-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-deepTechBlue-500 dark:text-light-50">
                  {user?.first_name
                    ? `${user.first_name} ${user.last_name}`
                    : 'Usuário'}
                </div>
                <div className="text-xs text-deepTechBlue-500 dark:text-light-400">
                  {user?.email || 'email@exemplo.com'}
                </div>
              </div>
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-graphite-800 rounded-md shadow-lg py-1 z-50 border border-light-200 dark:border-graphite-700">
                <Link
                  to="/subscription"
                  className="flex items-center w-full px-4 py-2 text-sm text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-700"
                  onClick={() => setShowUserMenu(false)}
                >
                  <CreditCard className="mr-3 h-4 w-4" />
                  {t('navigation.subscription')}
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center w-full px-4 py-2 text-sm text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-700"
                  onClick={() => setShowUserMenu(false)}
                >
                  <User className="mr-3 h-4 w-4" />
                  Perfil
                </Link>
                {canAccessOrganizationMenu && (
                  <Link
                    to="/organization"
                    className="flex items-center w-full px-4 py-2 text-sm text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-700"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Building2 className="mr-3 h-4 w-4" />
                    {t('organization.menu')}
                  </Link>
                )}
                <button className="flex items-center w-full px-4 py-2 text-sm text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-700">
                  <Settings className="mr-3 h-4 w-4" />
                  Configurações
                </button>
                <hr className="my-1 border-light-200 dark:border-graphite-700" />
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-deepTechBlue-500 dark:text-light-300 hover:bg-light-100 dark:hover:bg-graphite-700"
                >
                  <LogOut className="mr-3 h-4 w-4" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buy Tokens Modal */}
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />
    </header>
  );
};

export default Header;
